import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api/client";
import "./DataTable.css";

const RANGES = [
  { label: "1h", hours: 1 },
  { label: "6h", hours: 6 },
  { label: "12h", hours: 12 },
  { label: "24h", hours: 24 },
  { label: "48h", hours: 48 },
  { label: "7d", hours: 168 },
  { label: "전체", hours: 0 },
];

/**
 * Generic admin table: time-range chips, an hourly-count sparkline,
 * click-to-sort headers and offset pagination. Backed by the backend's
 * /api/admin/<list> and /api/admin/summary/<...> endpoints.
 */
export default function DataTable({ title, columns, listPath, summaryPath, defaultSort }) {
  const [hours, setHours] = useState(24);
  const [sort, setSort] = useState(defaultSort?.key || "");
  const [order, setOrder] = useState(defaultSort?.order || "desc");
  const [limit, setLimit] = useState(200);
  const [offset, setOffset] = useState(0);

  const [rows, setRows] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const canSort = (key) => columns.some((c) => c.key === key && c.sortable);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const qs = new URLSearchParams();
        qs.set("hours", String(hours));
        if (sort && canSort(sort)) qs.set("sort", sort);
        qs.set("order", order);
        qs.set("limit", String(limit));
        qs.set("offset", String(offset));

        const data = await apiFetch(`${listPath}?${qs.toString()}`);
        if (cancelled) return;
        setRows(data.rows || []);

        if (summaryPath) {
          const s = await apiFetch(`${summaryPath}?hours=${hours}`);
          if (!cancelled) setBuckets(s.buckets || []);
        } else {
          setBuckets([]);
        }
      } catch (e) {
        if (!cancelled) setErr(e.message || "불러오기 실패");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours, sort, order, limit, offset]);

  const maxCount = useMemo(
    () => Math.max(1, ...buckets.map((b) => Number(b.count) || 0)),
    [buckets]
  );

  const onSort = (key) => {
    if (!canSort(key)) return;
    if (sort === key) setOrder((o) => (o === "asc" ? "desc" : "asc"));
    else { setSort(key); setOrder("desc"); }
    setOffset(0);
  };

  return (
    <div className="dt-wrap">
      <div className="dt-head">
        <div className="dt-title">{title}</div>
        <div className="dt-range">
          {RANGES.map((r) => (
            <button
              key={r.label}
              className={`dt-chip ${hours === r.hours ? "active" : ""}`}
              onClick={() => { setHours(r.hours); setOffset(0); }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {summaryPath && (
        <div className="dt-summary">
          <div className="dt-summary-left">
            <div>
              <div className="dt-stat-label">불러온 행</div>
              <div className="dt-stat-value">{rows.length}</div>
            </div>
            <div>
              <div className="dt-stat-label">기간</div>
              <div className="dt-stat-value">{hours === 0 ? "전체" : `${hours}h`}</div>
            </div>
          </div>
          <div className="dt-spark">
            {buckets.map((b, i) => (
              <div
                key={i}
                className="dt-bar"
                title={`${b.bucket}: ${b.count}`}
                style={{ height: `${Math.round(((Number(b.count) || 0) / maxCount) * 100)}%` }}
              />
            ))}
          </div>
        </div>
      )}

      <div className="dt-controls">
        <div className="dt-left">
          <span className="dt-muted">정렬</span>
          <span className="dt-pill">{sort || "-"}</span>
          <span className="dt-pill">{order}</span>
        </div>
        <div className="dt-right">
          <span className="dt-muted">행/페이지</span>
          <select
            className="dt-select"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setOffset(0); }}
          >
            {[50, 100, 200, 500, 1000].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <button className="dt-btn" disabled={offset === 0}
            onClick={() => setOffset((o) => Math.max(0, o - limit))}>
            이전
          </button>
          <button className="dt-btn" disabled={rows.length < limit}
            onClick={() => setOffset((o) => o + limit)}>
            다음
          </button>
        </div>
      </div>

      {err && <div className="dt-error">오류: {err}</div>}

      {loading ? (
        <div className="dt-loading">불러오는 중…</div>
      ) : (
        <div className="dt-table-wrap">
          <table className="dt-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={col.sortable ? "sortable" : ""}
                    onClick={() => onSort(col.key)}
                  >
                    {col.label}
                    {col.sortable && sort === col.key && (
                      <span className="dt-arrow">{order === "asc" ? "▲" : "▼"}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id ?? i}>
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.render ? col.render(r[col.key], r) : fmtCell(r[col.key])}
                    </td>
                  ))}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td className="dt-empty" colSpan={columns.length}>데이터가 없습니다</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function fmtCell(v) {
  if (v === null || v === undefined) return "";
  return String(v);
}
